import React, { useRef, useState } from "react";
import { LuBookOpen, LuFileText, LuImagePlus, LuPlus, LuSparkles } from "react-icons/lu";
import { components } from "../../design/tokens";

interface AttachmentsBtnProps {
  onAddImageClick: () => void;
  onPromptLibraryClick?: () => void;
  onReferenceFilesClick?: () => void;
  onGradientBuilderClick?: () => void;
  className?: string;
  iconSize?: number;
  isDisabled?: boolean;
  dropdownPosition?: "top" | "bottom";
}

export const AttachmentsBtn: React.FC<AttachmentsBtnProps> = ({
  onAddImageClick,
  onPromptLibraryClick,
  onReferenceFilesClick,
  onGradientBuilderClick,
  className = "",
  iconSize = 24,
  isDisabled = false,
  dropdownPosition = "bottom",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAddImageClick = () => {
    setIsOpen(false);
    onAddImageClick();
  };

  const handlePromptLibraryClick = () => {
    setIsOpen(false);
    onPromptLibraryClick?.();
  };

  const handleGradientBuilderClick = () => {
    setIsOpen(false);
    onGradientBuilderClick?.();
  };

  const handleReferenceFilesClick = () => {
    setIsOpen(false);
    onReferenceFilesClick?.();
  };

  return (
    <div className="relative flex gap-4 items-center" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`${className} flex items-center justify-center`}
        type="button"
        disabled={isDisabled}
      >
        <LuPlus size={iconSize} />
      </button>

      {isOpen && (
        <div
          className={`absolute left-0 z-40 ${dropdownPosition === "top"
              ? "bottom-[calc(100%+8px)]"
              : "bottom-0"
            } ${components.dropdown.container}`}
        >
          <div role="menu" aria-orientation="vertical">
            <button
              id="attachments-btn-add-image"
              onClick={handleAddImageClick}
              className={components.dropdown.item}
              role="menuitem"
            >
              <LuImagePlus className="mr-3 h-5 w-5" />
              Add image
            </button>
            {onReferenceFilesClick && (
              <button
                id="attachments-btn-reference-files"
                onClick={handleReferenceFilesClick}
                className={components.dropdown.item}
                role="menuitem"
              >
                <LuFileText className="mr-3 h-5 w-5" />
                Reference files
              </button>
            )}
            {onGradientBuilderClick && (
              <button
                id="attachments-btn-open-gradient-builder"
                onClick={handleGradientBuilderClick}
                className={components.dropdown.item}
                role="menuitem"
              >
                <LuSparkles className="mr-3 h-5 w-5" />
                Gradient builder
              </button>
            )}
            {onPromptLibraryClick && (
              <button
                id="attachments-btn-open-prompt-library"
                onClick={handlePromptLibraryClick}
                className={components.dropdown.item}
                role="menuitem"
              >
                <LuBookOpen className="mr-3 h-5 w-5" />
                Prompt library
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
